import { useEffect, useState, useMemo } from "react";
import { Link, Navigate } from "react-router-dom";
import axios from "axios";
import {
    ArrowUpRight,
    Loader2,
    Folder,
    Briefcase,
    CheckSquare,
    AlertCircle,
    DollarSign,
    Mail,
    User,
    MessageSquare,
    FileText,
    CheckCircle2,
    Flag,
    Link2,
    FolderOpen,
    ExternalLink,
    Crown,
} from "lucide-react";
import { useClientAuth } from "@/lib/clientAuth";
import PortalShell from "@/components/PortalShell";
import ChatPanel from "@/components/ChatPanel";
import PortalGreeting from "@/components/PortalGreeting";
import NextActionCard from "@/components/NextActionCard";
import { getPlanTier } from "@/lib/planTier";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
    active: "text-emerald-400 border-emerald-400/40",
    on_hold: "text-amber-400 border-amber-400/40",
    review: "text-ember border-ember/40",
    completed: "text-bone-300 border-white/15",
    archived: "text-bone-500 border-white/10",
    draft: "text-bone-500 border-white/10",
};

export default function ClientDashboard() {
    const { client, token, loading, logout } = useClientAuth();
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(true);
    const [unreadChat, setUnreadChat] = useState(0);

    const headers = useMemo(
        () => ({ Authorization: `Bearer ${token}` }),
        [token],
    );

    useEffect(() => {
        if (!token) return;
        axios
            .get(`${API}/client/dashboard`, { headers })
            .then((r) => setData(r.data))
            .catch((err) => {
                if (err?.response?.status === 401) logout();
            })
            .finally(() => setBusy(false));
    }, [token, headers, logout]);

    // Poll the chat thread for unread messages — drives the "Next move" card.
    useEffect(() => {
        const clientId = data?.client?.id;
        if (!token || !clientId) return undefined;
        const fetchUnread = () => {
            axios
                .get(`${API}/chat/${clientId}/unread`, { headers })
                .then((r) => setUnreadChat(r.data?.unread || 0))
                .catch(() => {});
        };
        fetchUnread();
        const id = setInterval(fetchUnread, 15000);
        return () => clearInterval(id);
    }, [token, headers, data?.client?.id]);

    if (loading)
        return (
            <div className="min-h-screen bg-ink flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-ember" />
            </div>
        );
    if (!client) return <Navigate to="/portal/login" replace />;

    const stats = data?.stats || {};
    const projects = data?.projects || [];
    const activeProjects = projects.filter((p) =>
        ["active", "review", "on_hold"].includes(p.status),
    );
    const me = data?.client || client;
    const tier = getPlanTier(me?.plan);

    return (
        <PortalShell tier={tier}>
            <PortalGreeting client={me} />

            <NextActionCard
                tier={tier}
                unreadChat={unreadChat}
                needsApproval={stats.needs_approval || 0}
                outstanding={stats.outstanding || 0}
                managerName={me?.manager_name}
            />

            {/* Stat cards */}
            <section className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4 mb-12">
                <Stat
                    icon={Briefcase}
                    label="Active projects"
                    value={busy ? "—" : stats.active_projects ?? 0}
                    testid="stat-active-projects"
                />
                <Stat
                    icon={CheckSquare}
                    label="Milestones in progress"
                    value={busy ? "—" : stats.in_progress_milestones ?? 0}
                    testid="stat-in-progress"
                />
                <Stat
                    icon={AlertCircle}
                    label="Awaiting your approval"
                    value={busy ? "—" : stats.needs_approval ?? 0}
                    accent={stats.needs_approval > 0}
                    testid="stat-needs-approval"
                />
                <Stat
                    icon={DollarSign}
                    label="Outstanding"
                    value={
                        busy
                            ? "—"
                            : `$${(stats.outstanding ?? 0).toFixed(2)}`
                    }
                    testid="stat-outstanding"
                />
            </section>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 mb-12">
                {/* Account manager */}
                <Card
                    title="Your account manager"
                    className="lg:col-span-5"
                    testid="manager-card"
                >
                    {me.manager_name || me.manager_email ? (
                        <div>
                            <div className="flex items-center gap-4 mb-4">
                                <div className="w-12 h-12 rounded-full bg-ember/15 border border-ember/30 inline-flex items-center justify-center overflow-hidden">
                                    {me.manager_avatar_url ? (
                                        <img
                                            src={me.manager_avatar_url}
                                            alt={me.manager_name || "Manager"}
                                            onError={(e) => {
                                                e.currentTarget.style.display = "none";
                                                if (e.currentTarget.nextSibling) e.currentTarget.nextSibling.style.display = "block";
                                            }}
                                            className="w-full h-full object-cover"
                                        />
                                    ) : null}
                                    <User
                                        className="w-5 h-5 text-ember"
                                        style={{ display: me.manager_avatar_url ? "none" : "block" }}
                                    />
                                </div>
                                <div>
                                    <div className="font-display text-bone text-lg tracking-tight">
                                        {me.manager_name || "—"}
                                    </div>
                                    {me.manager_role && (
                                        <div className="text-bone-500 text-xs uppercase tracking-[0.2em]">
                                            {me.manager_role}
                                        </div>
                                    )}
                                </div>
                            </div>
                            {me.manager_email && (
                                <a
                                    href={`mailto:${me.manager_email}?subject=From the portal — ${encodeURIComponent(me.company || me.name || "")}`}
                                    data-testid="manager-email"
                                    className="btn-ember inline-flex items-center gap-2 h-11 px-4 text-xs uppercase tracking-[0.2em]"
                                >
                                    <Mail className="w-4 h-4" />
                                    Email {me.manager_name?.split(" ")[0] || "your manager"}
                                </a>
                            )}
                        </div>
                    ) : (
                        <p className="text-bone-500 text-sm">
                            Your account manager will appear here as soon as
                            they&apos;re assigned.
                        </p>
                    )}
                </Card>

                {/* Plan */}
                <Card
                    title="Your plan"
                    className={`lg:col-span-7 relative ${
                        tier === "sprint" || tier === "studio"
                            ? "border-gold/40 bg-gradient-to-br from-gold/[0.06] to-transparent overflow-hidden"
                            : ""
                    }`}
                    testid="plan-card"
                >
                    {(tier === "sprint" || tier === "studio") && (
                        <div className="absolute inset-0 pointer-events-none gold-shimmer" aria-hidden />
                    )}
                    <div className="relative">
                        {me.plan ? (
                            <div>
                                <div className="flex items-center gap-3 mb-2">
                                    {(tier === "sprint" || tier === "studio") && (
                                        <Crown className="w-4 h-4 text-gold" />
                                    )}
                                    <div
                                        className={`font-display text-2xl tracking-tight ${
                                            tier === "sprint" || tier === "studio"
                                                ? "text-gold"
                                                : "text-bone"
                                        }`}
                                    >
                                        {me.plan}
                                    </div>
                                </div>
                                {me.plan_summary && (
                                    <p className="text-bone-300 text-sm leading-relaxed whitespace-pre-wrap">
                                        {me.plan_summary}
                                    </p>
                                )}
                            </div>
                        ) : (
                            <p className="text-bone-500 text-sm">
                                Plan details will appear here once your engagement
                                is confirmed.
                            </p>
                        )}
                        <div className="mt-6 grid grid-cols-2 gap-px bg-white/10 border border-white/10">
                            <Mini
                                label="Invoices issued"
                                value={stats.invoice_count ?? 0}
                            />
                            <Mini
                                label="Paid to date"
                                value={`$${(stats.paid_total ?? 0).toFixed(2)}`}
                                highlight
                            />
                        </div>
                    </div>
                </Card>
            </div>

            {/* Active projects + activity */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8" id="projects">
                <section className="lg:col-span-7">
                    <div className="flex items-end justify-between mb-5 border-b border-white/10 pb-3">
                        <h2 className="font-display text-bone text-2xl tracking-tight">
                            Current projects
                        </h2>
                        <span className="text-xs uppercase tracking-[0.2em] text-bone-500">
                            {activeProjects.length} active
                        </span>
                    </div>
                    {busy ? (
                        <div className="text-bone-500 text-sm py-8 flex items-center gap-3">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Loading…
                        </div>
                    ) : activeProjects.length === 0 ? (
                        <div className="border border-white/10 bg-ink-800/40 p-10 text-center">
                            <Folder className="w-7 h-7 text-bone-500 mx-auto mb-3" />
                            <p className="text-bone-300 text-sm">
                                No active projects yet.
                            </p>
                        </div>
                    ) : (
                        <ul className="grid grid-cols-1 gap-3">
                            {activeProjects.map((p) => (
                                <li key={p.id}>
                                    <Link
                                        to={`/portal/projects/${p.id}`}
                                        data-testid={`portal-project-${p.id}`}
                                        className="group block border border-white/10 bg-ink-800/40 p-5 hover:border-ember/40 hover:-translate-y-0.5 transition-all"
                                    >
                                        <div className="flex items-start justify-between gap-4 mb-2">
                                            <span
                                                className={`text-[10px] uppercase tracking-[0.2em] border px-2 py-1 ${
                                                    STATUS_COLORS[p.status] ||
                                                    "text-bone-300 border-white/15"
                                                }`}
                                            >
                                                {p.status.replace("_", " ")}
                                            </span>
                                            <ArrowUpRight className="w-5 h-5 text-bone-500 group-hover:text-ember transition-colors" />
                                        </div>
                                        <h3 className="font-display text-bone text-xl tracking-tight">
                                            {p.title}
                                        </h3>
                                        {p.summary && (
                                            <p className="text-bone-300 text-sm leading-relaxed line-clamp-2 mt-1">
                                                {p.summary}
                                            </p>
                                        )}
                                        {p.milestone_total > 0 && (
                                            <div className="mt-4">
                                                <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.2em] text-bone-500 mb-1.5">
                                                    <span>Progress</span>
                                                    <span
                                                        className={
                                                            p.progress === 100
                                                                ? "text-emerald-400"
                                                                : "text-bone-300"
                                                        }
                                                    >
                                                        {p.progress}% ·{" "}
                                                        {p.milestone_done}/
                                                        {p.milestone_total}
                                                    </span>
                                                </div>
                                                <div className="h-1 bg-white/5 overflow-hidden">
                                                    <div
                                                        data-testid={`progress-${p.id}`}
                                                        className={`h-full transition-all duration-700 ${
                                                            p.progress === 100
                                                                ? "bg-emerald-400"
                                                                : "bg-ember"
                                                        }`}
                                                        style={{
                                                            width: `${p.progress}%`,
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    )}

                    {!busy && data?.shared_resources?.length > 0 && (
                        <section className="mt-10">
                            <div className="flex items-end justify-between mb-5 border-b border-white/10 pb-3">
                                <h2 className="font-display text-bone text-2xl tracking-tight">
                                    Shared resources
                                </h2>
                                <span className="text-xs uppercase tracking-[0.2em] text-bone-500">
                                    {data.shared_resources.length} latest
                                </span>
                            </div>
                            <ul
                                data-testid="shared-resources-list"
                                className="grid grid-cols-1 sm:grid-cols-2 gap-3"
                            >
                                {data.shared_resources.map((r) => (
                                    <li key={r.id}>
                                        <a
                                            href={r.url}
                                            target="_blank"
                                            rel="noreferrer"
                                            data-testid={`shared-resource-${r.id}`}
                                            className="group block border border-white/10 bg-ink-800/40 p-4 hover:border-sky-400/40 hover:-translate-y-0.5 transition-all"
                                        >
                                            <div className="flex items-start justify-between gap-3 mb-2">
                                                <span className="text-[10px] uppercase tracking-[0.2em] border border-sky-400/40 text-sky-400 px-2 py-1">
                                                    {r.kind}
                                                </span>
                                                <ExternalLink className="w-4 h-4 text-bone-500 group-hover:text-sky-400 transition-colors" />
                                            </div>
                                            <div className="font-display text-bone text-base tracking-tight line-clamp-2">
                                                {r.label}
                                            </div>
                                            <div className="text-bone-500 text-xs mt-1 truncate">
                                                {r.project_title} ·{" "}
                                                {timeAgo(r.created_at)}
                                            </div>
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    )}
                </section>

                <section className="lg:col-span-5">
                    <h2 className="font-display text-bone text-2xl tracking-tight mb-5 pb-3 border-b border-white/10">
                        Recent activity
                    </h2>
                    {busy ? (
                        <div className="text-bone-500 text-sm">Loading…</div>
                    ) : !data?.activity?.length ? (
                        <p className="text-bone-500 text-sm">
                            Comments, milestone updates and invoices will
                            appear here.
                        </p>
                    ) : (
                        <ul
                            data-testid="activity-feed"
                            className="space-y-3"
                        >
                            {data.activity.map((a, i) => {
                                const Icon =
                                    a.kind === "comment"
                                        ? MessageSquare
                                        : a.kind === "milestone"
                                          ? Flag
                                          : a.kind === "invoice_paid"
                                            ? CheckCircle2
                                            : a.kind === "resource"
                                              ? Link2
                                              : FileText;
                                const accent =
                                    a.kind === "invoice_paid"
                                        ? "text-emerald-400"
                                        : a.kind === "milestone"
                                          ? "text-ember"
                                          : a.kind === "resource"
                                            ? "text-sky-400"
                                            : a.by_type === "client"
                                              ? "text-ember"
                                              : "text-bone-500";
                                const Wrapper = a.url ? "a" : "li";
                                const wrapperProps = a.url
                                    ? {
                                          href: a.url,
                                          target: "_blank",
                                          rel: "noreferrer",
                                          "data-testid": `activity-resource-${i}`,
                                          className:
                                              "flex items-start gap-3 p-3 border border-white/10 bg-ink-800/40 hover:border-sky-400/40 transition-colors",
                                      }
                                    : {
                                          className:
                                              "flex items-start gap-3 p-3 border border-white/10 bg-ink-800/40",
                                      };
                                const content = (
                                    <>
                                        <Icon
                                            className={`w-4 h-4 mt-1 shrink-0 ${accent}`}
                                        />
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center justify-between gap-2 text-xs">
                                                <span className="text-bone truncate">
                                                    {a.by}
                                                    <span className="text-bone-500">
                                                        {" · "}
                                                        {a.title}
                                                    </span>
                                                </span>
                                                <span className="text-bone-500 shrink-0">
                                                    {timeAgo(a.at)}
                                                </span>
                                            </div>
                                            <div className="text-bone-300 text-xs mt-1 line-clamp-2">
                                                {a.preview}
                                            </div>
                                        </div>
                                        {a.url && (
                                            <ExternalLink className="w-3.5 h-3.5 mt-1 shrink-0 text-bone-500" />
                                        )}
                                    </>
                                );
                                return a.url ? (
                                    <li key={i}>
                                        <Wrapper {...wrapperProps}>
                                            {content}
                                        </Wrapper>
                                    </li>
                                ) : (
                                    <Wrapper key={i} {...wrapperProps}>
                                        {content}
                                    </Wrapper>
                                );
                            })}
                        </ul>
                    )}
                </section>
            </div>

            {me?.id && (
                <section
                    className="mt-10"
                    id="client-chat-section"
                    data-testid="client-chat-section"
                >
                    <div className="mb-5 flex items-center justify-between">
                        <h2 className="font-display text-bone text-2xl tracking-tight">
                            Conversation
                        </h2>
                        <span className="text-[11px] text-bone-500 uppercase tracking-[0.25em]">
                            {me.manager_name ? `with ${me.manager_name}` : "with the Aximo team"}
                        </span>
                    </div>
                    <div
                        className={
                            tier === "sprint" || tier === "studio"
                                ? "border border-gold/30 p-px bg-gradient-to-br from-gold/20 via-gold/[0.05] to-transparent"
                                : ""
                        }
                    >
                        <ChatPanel
                            clientId={me.id}
                            token={token}
                            actorType="client"
                            actorName={me.name}
                            sla={data?.sla}
                            title={me.manager_name ? `Chat with ${me.manager_name}` : "Chat with Aximo Studios"}
                            subtitle={
                                me.manager_role
                                    ? `${me.manager_role} · responses during business hours`
                                    : "Responses during business hours"
                            }
                        />
                    </div>
                </section>
            )}
        </PortalShell>
    );
}

function Card({ title, children, className = "", testid }) {
    return (
        <article
            data-testid={testid}
            className={`border border-white/10 bg-ink-800/40 p-6 ${className}`}
        >
            <h3 className="text-[10px] uppercase tracking-[0.25em] text-bone-500 mb-4">
                {title}
            </h3>
            {children}
        </article>
    );
}

function Stat({ icon: Icon, label, value, accent, testid }) {
    return (
        <div
            data-testid={testid}
            className={`p-5 border ${accent ? "border-ember/50 bg-ember/5" : "border-white/10 bg-ink-800/40"}`}
        >
            <Icon
                className={`w-4 h-4 mb-3 ${accent ? "text-ember" : "text-bone-500"}`}
            />
            <div
                className={`font-display text-3xl tracking-tightest ${accent ? "text-ember" : "text-bone"}`}
            >
                {value}
            </div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-bone-500 mt-2">
                {label}
            </div>
        </div>
    );
}

function Mini({ label, value, highlight }) {
    return (
        <div className="bg-ink-800 p-4">
            <div className="text-[10px] uppercase tracking-[0.25em] text-bone-500 mb-1">
                {label}
            </div>
            <div
                className={`font-display text-xl tracking-tight ${highlight ? "text-ember" : "text-bone"}`}
            >
                {value}
            </div>
        </div>
    );
}

function timeAgo(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    const seconds = Math.floor((Date.now() - d.getTime()) / 1000);
    if (seconds < 60) return "just now";
    const m = Math.floor(seconds / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const days = Math.floor(h / 24);
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString();
}
